import { useEffect, useState } from 'react';
import { Button, FormLabel, Group, Typography } from '@screentone/core';
import { useQueryClient } from '@tanstack/react-query';
import { JsonEditor } from 'json-edit-react';

import { useAlert } from 'contexts/alert/useAlert';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { PublicationSettingUpdateInput, usePublicationSettingUpdateMutation } from 'data/generated/graphql';
import QueryRuleSchemaEditor from 'features/publication-settings/components/QueryRuleSchemaEditor';
import { useConvertedProperty } from 'hooks';
import { GLOBAL_PUBLICATION_KEY, usePublicationSettings } from 'hooks/publication-settings';
import SiteConfigPage from 'pages/site-config/shared/SiteConfigPage';
import styles from './styles.module.scss';

function SiteConfigQueryRules() {
  const currentProperty = useConvertedProperty();
  const queryClient = useQueryClient();
  const { alertError, alertSuccess } = useAlert();
  const { state: lockState } = usePageLock();
  const { isReadOnly } = lockState.publishing;

  const [queryRuleSchemaValue, setQueryRuleSchemaValue] = useState<Record<string, unknown> | null | undefined>();
  const [defaultQueryConfigs, setDefaultQueryConfigs] = useState<Record<string, unknown> | null | undefined>();
  const [hasChanged, setHasChanged] = useState(false);

  const publicationSettingsQueryVariables = { publicationKey: currentProperty ?? '' };

  const { data: publicationSettingsResponse } = usePublicationSettings(publicationSettingsQueryVariables);
  const { data: globalPublicationSettingsResponse } = usePublicationSettings({
    publicationKey: GLOBAL_PUBLICATION_KEY
  });

  const { mutate, isLoading: isUpdating } = usePublicationSettingUpdateMutation();

  useEffect(() => {
    if (publicationSettingsResponse) {
      const {
        publicationSetting: { queryRuleSchema, defaultQueryConfigs: fetchedDefaultQueryConfigs }
      } = publicationSettingsResponse;
      setQueryRuleSchemaValue(queryRuleSchema);
      setDefaultQueryConfigs(fetchedDefaultQueryConfigs);
      setHasChanged(false);
    }
  }, [publicationSettingsResponse]);

  const handleSave = () => {
    if (!publicationSettingsResponse || !currentProperty) return;

    const publicationSettingUpdateInput = {
      ...publicationSettingsResponse.publicationSetting,
      queryRuleSchema: queryRuleSchemaValue,
      defaultQueryConfigs
    } as PublicationSettingUpdateInput;

    mutate(
      { publicationSettingUpdateInput },
      {
        onSuccess: async () => {
          setHasChanged(false);
          alertSuccess('Query rules settings saved successfully');
          await queryClient.invalidateQueries({
            queryKey: ['PublicationSettings', publicationSettingsQueryVariables],
            exact: false
          });
        },
        onError: (error) => {
          alertError(`Failed to save query rules settings. ${(error as Error).message}`);
        }
      }
    );
  };

  const isReady = !!publicationSettingsResponse && !!globalPublicationSettingsResponse;

  const saveAction = isReady ? (
    <Button primary onClick={handleSave} disabled={!hasChanged || isUpdating || isReadOnly}>
      Save
    </Button>
  ) : null;

  return (
    <SiteConfigPage title="Query Rules" actions={saveAction}>
      {isReady ? (
        <Group gap="lg" direction="column" fullWidth>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="Query rule schema" labelPosition="top" className={styles.inputForm} />
            <Typography variant="note">
              Schema for content-parameter filters allowed in query rules (Author, DocType, etc.). Read by the
              query-rules form on section-edit pages.
            </Typography>
            {queryRuleSchemaValue && (
              <QueryRuleSchemaEditor
                data={queryRuleSchemaValue}
                onChange={(newValue) => {
                  setQueryRuleSchemaValue(newValue);
                  setHasChanged(true);
                }}
                globalQueryRuleSchema={globalPublicationSettingsResponse.publicationSetting.queryRuleSchema}
                isReadOnly={isReadOnly}
              />
            )}
          </Group>
          <Group gap="xs" direction="column" fullWidth>
            <FormLabel label="Default query configs" labelPosition="top" className={styles.inputForm} />
            <Typography variant="note">
              Per-(treatment-type) starter queries used by the &quot;add query&quot; context menu.
            </Typography>
            <JsonEditor
              data={defaultQueryConfigs}
              indent={2}
              setData={(value) => {
                setDefaultQueryConfigs(value as Record<string, unknown>);
                setHasChanged(true);
              }}
              maxWidth="100%"
              collapse
              restrictEdit={isReadOnly}
              restrictDelete={isReadOnly}
              restrictAdd={isReadOnly}
            />
          </Group>
        </Group>
      ) : (
        <Typography variant="note">Loading…</Typography>
      )}
    </SiteConfigPage>
  );
}

export default SiteConfigQueryRules;
